// Number formats from the design system: money `₮14 800 000` (space-grouped thousands),
// short money `₮48.2сая`, sizes always in millimetres, dates `11/08`, times `19:00`.

const NBSP = "\u00a0"; // no-break space: "14 800 000" never wraps inside an amount

function group(n: number): string {
  const sign = n < 0 ? "-" : "";
  const digits = Math.abs(Math.trunc(n)).toString();
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Whole tögrög, space grouped: formatMNT(14800000) === "₮14 800 000". */
export function formatMNT(amount: number): string {
  if (!Number.isFinite(amount)) throw new RangeError(`not a finite amount: ${amount}`);
  const rounded = Math.round(amount);
  return rounded < 0 ? `-₮${group(-rounded)}` : `₮${group(rounded)}`;
}

/** Compact money for KPIs: ₮48.2сая, ₮1.2тэрбум, ₮950мянга; below 1000 the full amount. */
export function formatMNTShort(amount: number): string {
  if (!Number.isFinite(amount)) throw new RangeError(`not a finite amount: ${amount}`);
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  const units: [number, string][] = [
    [1e9, "тэрбум"],
    [1e6, "сая"],
    [1e3, "мянга"],
  ];
  const short = (size: number) => Math.round((abs / size) * 10) / 10;
  for (const [i, [size, word]] of units.entries()) {
    if (abs < size) continue;
    // 999 950 rounds to 1000мянга: show it in the next unit up instead (₮1сая).
    const bigger = units[i - 1];
    if (short(size) >= 1000 && bigger) return `${sign}₮${short(bigger[0]).toString()}${bigger[1]}`;
    return `${sign}₮${short(size).toString()}${word}`;
  }
  return formatMNT(amount);
}

/** Plain count with grouped thousands: 1 284. */
export function formatCount(n: number): string {
  return group(n);
}

/** Millimetre sizes in order length × width × thickness: "2800 × 2070 × 18 мм". */
export function formatDimensions(values: readonly number[], unit = "мм"): string {
  return `${values.map((v) => group(v).replaceAll(NBSP, "")).join(" × ")} ${unit}`;
}

/** Short date `11/08` (month/day) in Ulaanbaatar time. */
export function formatShortDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Ulaanbaatar",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("month")}/${get("day")}`;
}

/** 24-hour time `19:00` in Ulaanbaatar time. */
export function formatTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Ulaanbaatar",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);
}

/** Two initials for an avatar: "Номин Тав" -> "НТ". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? "";
  return (first + second).toLocaleUpperCase("mn");
}
